import { ArrowRight, Headphones, RadioTower } from "lucide-react";
import { CallTile } from "./call-tile";
import type { EngagementTier, SupervisedCall } from "../types";
import type { TranscriptLanguageMode } from "../lib/transcript-language";

/** djb2-ish hash of a string — same as in call-tile, kept local to avoid a shared util dep. */
function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return Math.abs(h);
}

/**
 * Calculates optimal grid dimensions (columns and rows) so visible call cards
 * expand to completely cover the wall area (both width and height) without dead whitespace.
 */
export function getGridDimensions(count: number): { cols: number; rows: number } {
  // Never larger than a 3x2 cell: a lone call filled the screen with tile-sized text.
  if (count <= 6) return { cols: 3, rows: 2 };
  if (count <= 8) return { cols: 4, rows: 2 };
  if (count <= 9) return { cols: 3, rows: 3 };
  if (count <= 12) return { cols: 4, rows: 3 };
  if (count <= 16) return { cols: 4, rows: 4 };
  if (count <= 20) return { cols: 5, rows: 4 };
  return { cols: 5, rows: Math.ceil(count / 5) };
}

/**
 * The wall: every live call on the account, ranked by what wants attention.
 *
 * Sized for dense supervision: the first ten calls remain visible on ordinary laptop
 * viewports, while the page's wall column scrolls through up to forty live calls. It is
 * deliberately not virtualized -- at this count that would add more scroll complexity than
 * it removes.
 */
export function CallWall({
  calls,
  selectedCallId,
  connectedCallId,
  tier,
  currentUserId,
  sliced,
  onSelect,
  onOpenQueue,
  languageMode,
  maxCalls,
  isDemoMode,
}: {
  calls: SupervisedCall[];
  selectedCallId: string | null;
  connectedCallId: string | null;
  tier: EngagementTier;
  currentUserId?: string | null;
  /** True when a slice is showing and calls are held back -- the empty state says so. */
  sliced?: boolean;
  onSelect: (callId: string) => void;
  onOpenQueue: () => void;
  languageMode: TranscriptLanguageMode;
  maxCalls?: number;
  /**
   * DEMO MODE ONLY. When true each card receives a stable per-call seed that drives
   * the animated HSL accent colour cycling in CallTile.
   * This flag must NEVER be set outside of demo mode.
   */
  isDemoMode?: boolean;
}) {
  if (!calls.length) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-elevated/60 p-10 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-soft text-ink-3"><Headphones size={24} /></span>
        <p className="text-base font-semibold text-ink">
          {sliced ? "Nothing assigned to you" : "No live calls"}
        </p>
        <p className="mt-1 max-w-sm text-sm text-ink-3">
          {sliced
            ? "Your colleagues hold the floor. Flip to the whole floor to look anyway."
            : "Calls appear here automatically as batches dial out."}
        </p>
        <span className="mt-5 flex items-center gap-2 rounded-full border border-line bg-elevated px-3 py-1.5 text-[11px] text-ink-3"><RadioTower size={12} /> Monitoring for new activity</span>
      </div>
    );
  }

// Constrain visible call cards to the supervisor's comfortable watch capacity (min 1, max 25).
  const limit = Math.max(1, Math.min(25, maxCalls ?? 25));
  const shown = calls.slice(0, limit);
  const overflow = calls.length - shown.length;
  const { cols, rows } = getGridDimensions(shown.length);

  return (
    <div data-testid="supervision-call-wall" className="flex flex-1 flex-col min-h-0 h-full w-full">
      <div
        className="grid gap-2 w-full h-full flex-1 min-h-0"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
      >
      {shown.map((call) => (
        <CallTile
          key={call.call_id}
          call={call}
          selected={call.call_id === selectedCallId}
          connected={call.call_id === connectedCallId}
          tier={tier}
          currentUserId={currentUserId}
          onSelect={onSelect}
          languageMode={languageMode}
          demoSeed={call.is_demo ? hashStr(call.call_id) : undefined}
        />
      ))}
      </div>
      {overflow > 0 ? (
        <button
          type="button"
          onClick={onOpenQueue}
          className="mt-2 shrink-0 flex w-full items-center justify-center gap-2 rounded-xl border border-line bg-elevated px-4 py-2 text-xs font-medium text-ink-2 transition hover:border-brand/50 hover:text-ink"
          data-testid="supervision-wall-overflow"
        >
          +{overflow} more {overflow === 1 ? "call" : "calls"} <span className="text-ink-3">—</span> Open queue <ArrowRight size={13} />
        </button>
      ) : null}
    </div>
  );
}
